import { atom, selector } from 'recoil';
import { effectConsoleLogAtomUpdates } from '../helpers/debug';
import type { VineCartProps, ContentfulOfferProps } from 'tsconfig/types.d';
import { globals, GENERIC_ERROR_MESSAGE } from 'common-ui';

export const commerceApiExtraConfig = (publicCsrfValue = ''): { webApiExtraHeaders: { [key: string]: string } } => ({
  webApiExtraHeaders: {
    [globals.authAndWebApiCsrfHeaderName as string]: publicCsrfValue,
  },
});

//Atom
//----------------------------------------------------------------------
export const cartAtom = atom<VineCartProps>({
  key: 'cartAtom', // unique ID (with respect to other atoms/selectors)
  default: {
    cartIsLoaded: false,
    cartItems: undefined,
    cartError: {
      hasCartError: false,
      cartErrorMessage: '',
    },
  }, // default value (aka initial value)
  effects: [effectConsoleLogAtomUpdates],
});

export const subscribeSaveContentfulOffer = atom<ContentfulOfferProps[] | null>({
  key: 'subscribeSaveContentfulOffer',
  default: null,
});

//Selectors
//----------------------------------------------------------------------
export const selectCartDetails = selector({
  key: 'selectCartDetails', // unique ID (with respect to other atoms/selectors)
  get: ({ get }) => {
    const { cartItems }: VineCartProps = get(cartAtom);

    if (!cartItems?.totalLineItems) {
      return {
        itemsCount: 0,
        cartTotal: 0,
        hasQuantity: false,
      };
    }

    let cartTotal = cartItems.totalPrice.amount ?? 0;

    // Adjust cart total if using new AWS cart API and shipping information is present
    if (globals.useNewCartAPI) {
      const shippingPrice = cartItems?.shippingInformation?.price?.amount ?? 0;
      cartTotal = Math.max(cartTotal - shippingPrice, 0);
    }

    return {
      itemsCount: cartItems.totalLineItems,
      cartTotal,
      hasQuantity: cartItems.totalLineItems > 0,
    };
  },
});

/**
 * Cart data selector.
 *
 * This selector retrieves the cart data from the Recoil state.
 *
 * - key: 'cartData'
 * - get: Function - Fetches cart data from 'cartAtom' atom.
 */
export const selectCartData = selector({
  key: 'selectCartData',
  get: ({ get }) => {
    try {
      return get(cartAtom);
    } catch (error) {
      throw new Error(GENERIC_ERROR_MESSAGE);
    }
  },
});
